import { getRequest, putRequest, postRequest } from './api';

/**
 * Data Agent configuration service.
 *
 * The data-service has no JWT layer; the FE asserts is_admin from
 * AuthContext when calling admin-only endpoints. The gateway is
 * responsible for authenticating users before requests reach the
 * data-service.
 */

/** Returns { agent, fields, enum_sources } */
export async function getSchema() {
  return await getRequest('data', '/agent-config/data/schema');
}

/**
 * Returns { effective, system_overrides, user_overrides }
 *
 * @param {string|null} userEmail
 */
export async function getConfig(userEmail) {
  return await getRequest('data', '/agent-config/data', userEmail ? { user_email: userEmail } : {});
}

/**
 * Admin-only — write system-level overrides.
 *
 * @param {string} actorEmail
 * @param {object} updates - { config_key: value }
 */
export async function putSystemConfig(actorEmail, updates) {
  return await putRequest('data', '/agent-config/data', {
    actor_email: actorEmail,
    is_admin: true,
    updates,
  });
}

/**
 * User path — write user-level overrides for the current user.
 * Only allowlisted keys will be accepted by the backend.
 *
 * @param {string} actorEmail
 * @param {object} updates - { config_key: value }
 */
export async function putUserConfig(actorEmail, updates) {
  return await putRequest('data', '/agent-config/data/me', {
    actor_email: actorEmail,
    updates,
  });
}

/**
 * Reset overrides to defaults.
 *
 * @param {string} actorEmail
 * @param {boolean} isAdmin
 * @param {'system'|'user'} level
 * @param {string|null} ownerEmail - required when level='user'
 * @param {string[]} keys - empty array = reset everything at this scope
 */
export async function resetConfig(actorEmail, isAdmin, level, ownerEmail, keys = []) {
  return await postRequest('data', '/agent-config/data/reset', {
    actor_email: actorEmail,
    is_admin: !!isAdmin,
    level,
    owner_email: ownerEmail,
    keys,
  });
}

/** Admin-only — return recent config changes. */
export async function getAudit(isAdmin, limit = 200) {
  return await getRequest('data', '/agent-config/data/audit', {
    is_admin: isAdmin ? 'true' : 'false',
    limit,
  });
}

/** Admin-only — list recent agent runs. */
export async function getTelemetryRuns(isAdmin, opts = {}) {
  return await getRequest('data', '/agent-config/data/telemetry/runs', {
    is_admin: isAdmin ? 'true' : 'false',
    limit: opts.limit ?? 100,
    offset: opts.offset ?? 0,
    ...(opts.promptVersion ? { prompt_version: opts.promptVersion } : {}),
    ...(opts.userEmail ? { user_email: opts.userEmail } : {}),
  });
}

/** Admin-only — aggregate stats grouped by prompt_version. */
export async function getTelemetryStats(isAdmin, limit = 50) {
  return await getRequest('data', '/agent-config/data/telemetry/stats', {
    is_admin: isAdmin ? 'true' : 'false',
    limit,
  });
}

const agentConfigService = {
  getSchema,
  getConfig,
  putSystemConfig,
  putUserConfig,
  resetConfig,
  getAudit,
  getTelemetryRuns,
  getTelemetryStats,
};

export default agentConfigService;
